//RolesPermissions Component: Displays list of roles and permissions across projects, allows searching and filtering of roles, and shows how many users are assigned to each role.
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Chip,
  Stack,
  TextField,
  ThemeProvider,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import axios from "axios";
import { getUsers, projectsApi } from "../../apiCalls/Apicalls";
import { theme } from "../../utilities/dataGridHelpers";

const levelStyles = {
  Admin: {
    bgcolor: "rgba(65, 139, 255, 0.12)",
    color: "#418BFF",
  },
  Lead: {
    bgcolor: "rgba(245, 158, 11, 0.1)",
    color: "#f59e0b",
  },
  Viewer: {
    bgcolor: "#eef1f7",
    color: "#6b7280",
  },
  Contributor: {
    bgcolor: "rgba(34, 197, 94, 0.1)",
    color: "#22c55e",
  },
};

function RolesPermissions() {
  const [search, setSearch] = useState("");
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);

  useEffect(() => {
    Promise.allSettled([axios.get(projectsApi), axios.get(getUsers)]).then(
      (results) => {
        if (results[0].status === "fulfilled") {
          setProjects(results[0].value.data || []);
        }
        if (results[1].status === "fulfilled") {
          setUsers(results[1].value.data || []);
        }
      },
    );
  }, []);

  const rows = useMemo(() => {
    const assignmentCount = users.reduce((acc, user) => {
      (user?.Access || []).forEach((label) => {
        acc[label] = (acc[label] || 0) + 1;
      });
      return acc;
    }, {});

    const list = projects.flatMap((project) =>
      (project.accessCatalog || []).map((entry) => ({
        id: entry.label,
        roleCode: entry.label,
        project: project.name,
        level:
          entry.role.includes("ADMIN") || entry.role === "ADMIN"
            ? "Admin"
            : entry.role.includes("TL")
              ? "Lead"
              : entry.role.includes("VIEW")
                ? "Viewer"
                : "Contributor",
        description:
          entry.value || `${entry.role} access template for ${project.name}`,
        assigned: assignmentCount[entry.label] || 0,
      })),
    );

    if (!search.trim()) {
      return list;
    }

    const query = search.toLowerCase();
    return list.filter((item) =>
      `${item.roleCode} ${item.project} ${item.level} ${item.description}`
        .toLowerCase()
        .includes(query),
    );
  }, [projects, search, users]);

  const columns = [
    {
      field: "roleCode",
      headerName: "Role Code",
      flex: 1.25,
      minWidth: 190,
      renderCell: (params) => (
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: "0.84rem",
            color: "var(--ax-ink)",
            fontFamily: "JetBrains Mono, Consolas, monospace",
          }}
        >
          {params.value}
        </Typography>
      ),
    },
    { field: "project", headerName: "Project", flex: 1.2, minWidth: 180 },
    {
      field: "level",
      headerName: "Level",
      flex: 0.8,
      minWidth: 120,
      renderCell: (params) => {
        const style = levelStyles[params.value] || levelStyles.Viewer;
        return (
          <Chip
            label={params.value}
            size="small"
            sx={{
              bgcolor: style.bgcolor,
              color: style.color,
              fontWeight: 500,
              fontSize: "0.72rem",
              borderRadius: "6px",
            }}
          />
        );
      },
    },
    {
      field: "description",
      headerName: "Description",
      flex: 1.6,
      minWidth: 260,
      renderCell: (params) => (
        <Typography
          sx={{
            fontSize: "0.84rem",
            color: "var(--ax-ink-2)",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {params.value}
        </Typography>
      ),
    },
    {
      field: "assigned",
      headerName: "Users Assigned",
      flex: 0.85,
      minWidth: 150,
      renderCell: (params) => (
        <Stack direction="row" alignItems="center" spacing={0.75}>
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: "0.86rem",
              color: "var(--ax-ink)",
            }}
          >
            {params.value}
          </Typography>
          <Typography sx={{ fontSize: "0.78rem", color: "var(--ax-ink-3)" }}>
            users
          </Typography>
        </Stack>
      ),
    },
  ];

  return (
    <div className="ax-page-grid">
      <div className="ax-card ax-table-shell" style={{ padding: 0 }}>
        <div className="ax-grid-toolbar">
          <Typography
            sx={{
              color: "var(--ax-ink-2)",
              fontSize: "0.86rem",
            }}
          >
            <Typography
              component="span"
              sx={{
                fontWeight: 600,
                color: "var(--ax-ink)",
                fontSize: "0.86rem",
              }}
            >
              {rows.length}
            </Typography>{" "}
            role definitions across{" "}
            <Typography
              component="span"
              sx={{
                fontWeight: 600,
                color: "var(--ax-ink)",
                fontSize: "0.86rem",
              }}
            >
              {projects.length}
            </Typography>{" "}
            projects
          </Typography>
          <TextField
            placeholder="Search roles or projects..."
            size="small"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            sx={{ minWidth: { xs: "100%", sm: 280 } }}
          />
        </div>

        <Box sx={{ height: "calc(100vh - 240px)", minHeight: 420 }}>
          <ThemeProvider theme={theme}>
            <DataGrid
              disableColumnMenu
              disableRowSelectionOnClick
              rows={rows}
              columns={columns}
              rowHeight={62}
              pageSizeOptions={[10, 25, 50]}
              initialState={{
                pagination: {
                  paginationModel: { pageSize: 10 },
                },
              }}
              slots={{
                noRowsOverlay: () => (
                  <Box
                    sx={{
                      display: "grid",
                      placeItems: "center",
                      height: "100%",
                    }}
                  >
                    <Typography
                      sx={{
                        color: "var(--ax-ink-3)",
                        fontSize: "0.86rem",
                      }}
                    >
                      No roles or permissions found
                    </Typography>
                  </Box>
                ),
              }}
            />
          </ThemeProvider>
        </Box>
      </div>
    </div>
  );
}

export default RolesPermissions;
